import { format } from "date-fns";
import React from "react";

import { DEFAULT_HELICAL_LOG, ROWS_PER_PAGE } from 'helpers/pileLogs';
import LogsReportProjectInfo from "./LogsReportProjectInfo";

const columnsToRows = (columns, maxDepthRows) => {
  const keys = _.keys(DEFAULT_HELICAL_LOG);
  return columns.reduce((rows, column) => {
    keys.map((key) => {
      rows[key] = (rows[key] || []);
      rows[key].push(column[key]);
    });
    rows.measurementRows = (rows.measurementRows || []);
    _.times(maxDepthRows, (n) => {
      rows.measurementRows[n] = (rows.measurementRows[n] || []);
      rows.measurementRows[n].push(column.measurements && column.measurements[n]);
    });
    return rows;
  }, {});
};

const rowsFor = (pageNumber) => {
  if (pageNumber === 1) return [0, 14];

  const start = 14 + ((pageNumber - 2) * ROWS_PER_PAGE);
  return [start, start + ROWS_PER_PAGE];
};

const measurementValues = (row) => {
  return _.compact([
    _.get(row, 1),
    _.get(row, 2),
  ]).join(' / ');
};

export default function HelicalLogReportPage({page, project}) {
  const rows = columnsToRows(page.piles, page.maxDepthRows);
  const blankColumns = 6 - rows.pileNo.length;
  const firstPage = page.pageNumber === 1;
  const lastPage = page.pageNumber === page.numberOfPages;
  const measurementRows = _.slice(rows.measurementRows, ...rowsFor(page.pageNumber));

  return <>
    {firstPage && <LogsReportProjectInfo project={project} helical/>}
    <table className="report-table helical">
      <tbody>
        <tr>
          {firstPage ? (
            <th rowSpan={6} className="table-header-vertical">Pile Information</th>
          ) : (
            <th></th>
          )}
          <th className="row-header">Pile Number</th>
          {rows.pileNo.map((pileNo, i) => <th key={i}>{pileNo}</th>)}
          {_.times(blankColumns, (j) => <th key={j} className="blank-column"></th>)}
        </tr>

        {firstPage && <>
          <tr>
            <th>Start Time</th>
            {rows.startTime.map((startTime, i) => (
              <td key={i}>{startTime && format(startTime, 'Pp')}</td>)
            )}
          </tr>
          <tr>
            <th>Completion Time</th>
            {rows.completionTime.map((completionTime, i) => (
              <td key={i}>{completionTime && format(completionTime, 'Pp')}</td>)
            )}
          </tr>
          <tr>
            <th>Pile Shaft Designation</th>
            {rows.pileShaft.map((pileShaft, i) => <td key={i}>{pileShaft}</td>)}
          </tr>
          <tr>
            <th>Pile Length</th>
            {rows.pileLength.map((pileLength, i) => <td key={i}>{pileLength}</td>)}
          </tr>
          <tr>
            <th>Lead Helices</th>
            {rows.leadHelices.map((leadHelices, i) => <td key={i}>{leadHelices}</td>)}
          </tr>
        </>}

        <tr>
          <th rowSpan={measurementRows.length + 1} className="table-header-vertical">
            Periodic Torque Measurements
            {page.pageNumber > 1 && " (Continued)"}
          </th>
          <th>Depth (ft)</th>
          <th colSpan={rows.pileNo.length}>Torque (ft-lbs) / (psi)</th>
        </tr>
        {measurementRows.map((col, i) => (
          <tr key={i} className="measurement-row">
            <th>
              {_.get(col, [0, 0])}
              {!lastPage && page.numberOfPages > 1 && i === measurementRows.length - 1 && "*"}
            </th>
            {_.map(measurementRows[i], (row, k) => (
              <td key={k}>{measurementValues(row)}</td>
            ))}
          </tr>
        ))}

        {(page.pageNumber === 1 || page.numberOfPages === 1) && <>
          <tr>
            <th rowSpan={5} className="table-header-vertical">Deviations</th>
            <th>North-South Deviation (in)</th>
            {rows.deviationNS.map((deviationNS, i) => <td key={i}>{deviationNS}</td>)}
          </tr>
          <tr>
            <th>East-West Deviation (in)</th>
            {rows.deviationEW.map((deviationEW, i) => <td key={i}>{deviationEW}</td>)}
          </tr>
          <tr>
            <th>Vertical Deviation (in)</th>
            {rows.deviationV.map((deviationV, i) => <td key={i}>{deviationV}</td>)}
          </tr>
          <tr>
            <th>Planned Inclination Angle</th>
            {rows.plannedInclination.map((plannedInclination, i) => <td key={i}>{plannedInclination}</td>)}
          </tr>
          <tr>
            <th>Measured Inclination Angle</th>
            {rows.measuredInclination.map((measuredInclination, i) => <td key={i}>{measuredInclination}</td>)}
          </tr>
        </>}
        {firstPage && <>
          <tr>
            <th rowSpan={7} className="table-header-vertical">Termintation Data</th>
            <th>Required Torque</th>
            {rows.requiredTorque.map((requiredTorque, i) => <td key={i}>{requiredTorque}</td>)}
          </tr>
          <tr>
            <th>Final Torque</th>
            {rows.finalTorque.map((finalTorque, i) => <td key={i}>{finalTorque}</td>)}
          </tr>
          <tr>
            <th>Anticipated Depth (ft)</th>
            {rows.anticipatedDepth.map((anticipatedDepth, i) => <td key={i}>{anticipatedDepth}</td>)}
          </tr>
          <tr>
            <th>Minimum Depth (ft)</th>
            {rows.minimumDepth.map((minimumDepth, i) => <td key={i}>{minimumDepth}</td>)}
          </tr>
          <tr>
            <th>Final Depth (ft)</th>
            {rows.finalDepth.map((finalDepth, i) => <td key={i}>{finalDepth}</td>)}
          </tr>
          <tr>
            <th>Above Ground (ft)</th>
            {rows.aboveGround.map((aboveGround, i) => <td key={i}>{aboveGround}</td>)}
          </tr>
          <tr>
            <th>Criteria Met?</th>
            {rows.criteriaMet.map((criteriaMet, i) => <td key={i}>{criteriaMet ? <b>YES</b> : 'NO'}</td>)}
          </tr>
        </>}
      </tbody>
    </table>

    {page.numberOfPages > 1 && !lastPage && <div>*Continued (next page)</div>}

    {firstPage && <>
      <div className="grid">
        <div className="col-1-2 pile-comments-section">
          <h4>Comments:</h4>
          {page.piles.map(({pileNo, comments}) => _.isPresent(comments) && <React.Fragment key={pileNo}>
            <p><em>Pile {pileNo}:</em> {comments}</p>
          </React.Fragment>)}
        </div>
        <div className="col-1-2">
          <p>
            Piles marked "YES" for "Criteria Met?" were installed in
            general accordance with project documents.
          </p>
          <p className="signature-line">Signature:</p>
        </div>
      </div>
    </>}
  </>;
}
